import { cloneDeep, isEqual } from "lodash";
import * as client from "../../../client";

export const isValidEffectiveDate = (effectiveDate: string) => {
  // Check for mm/dd/yyyy format
  const dateFormatRegex = /^\d{2}\/\d{2}\/\d{4}$/;
  return dateFormatRegex.test(effectiveDate);
};

export type DBPerilType = client.PolicyInfo["included_perils"][number];

export const readableNameFromPeril = (peril: DBPerilType): string => {
  switch (peril) {
    case "aop":
      return "All Other Perils";
    case "earthquake":
      return "Earthquake";
    case "earth_movement":
      return "Earth Movement";
    case "flood":
      return "Flood";
    case "named_storm":
      return "Named Storm";
    case "scs":
      return "Wind / Severe Convective Storm";
    case "wildfire":
      return "Wildfire";
    case "equipment_breakdown":
      return "Equipment Breakdown";
    case "wind_and_hail":
      return "Wind and Hail (Legacy)";
  }
};

export const perilSentenceFromEnabledPerils = (
  perils: DBPerilType[],
  perilSentenceOverride: string | null | undefined
): string => {
  if (perilSentenceOverride) {
    return perilSentenceOverride;
  }

  const has = (p: client.peril_type) => perils.includes(p);
  const isAop = has("aop");

  // Resolve earthquake vs earth_movement (mutually exclusive).
  // earth_movement is the broader peril and takes priority.
  // If neither is present, we show "excluding Earthquake".
  const resolvedEarthPeril: { label: string; included: boolean } = has(
    "earth_movement"
  )
    ? { label: "Earth Movement", included: true }
    : has("earthquake")
      ? { label: "Earthquake", included: true }
      : { label: "Earthquake", included: false };

  // Perils always mentioned in AOP mode (in sentence order).
  const alwaysMentioned: { label: string; included: boolean }[] = [
    resolvedEarthPeril,
    { label: "Flood", included: has("flood") },
    { label: "Equipment Breakdown", included: has("equipment_breakdown") },
  ];

  // ──────────────────────────────────────
  // "X Only" — AOP is NOT present
  // ──────────────────────────────────────
  if (!isAop) {
    const enabledLabels = perils
      .filter((p) => p !== "aop")
      // Drop earthquake if earth_movement is present (already covered)
      .filter((p) => !(p === "earthquake" && has("earth_movement")))
      .map((p) => readableNameFromPeril(p));

    if (enabledLabels.length === 0) {
      return perils.join(", ");
    }

    return `${joinWithAnd(enabledLabels)} Only`;
  }

  // ──────────────────────────────────────
  // "All risks …" — AOP IS present
  // Group consecutive perils that share the same include/exclude status.
  // ──────────────────────────────────────
  type Clause = { type: "including" | "excluding"; perils: string[] };
  const clauses: Clause[] = [];

  for (const { label, included } of alwaysMentioned) {
    const clauseType = included ? "including" : "excluding";

    const lastClause = clauses[clauses.length - 1];
    if (lastClause && lastClause.type === clauseType) {
      lastClause.perils.push(label);
    } else {
      clauses.push({ type: clauseType, perils: [label] });
    }
  }

  let sentence = "All risks";
  for (const clause of clauses) {
    sentence += `, ${clause.type} ${joinWithAnd(clause.perils)}`;
  }

  return sentence;
};

function joinWithAnd(items: string[]): string {
  if (items.length <= 2) {
    return items.join(" and ");
  }
  return items.slice(0, -1).join(", ") + ", and " + items[items.length - 1];
}

/**
 * The company_info to save after editing the overrides-applied view of it.
 * Each field that differs from the saved view becomes a private override; a
 * field edited back to its computed value drops its override instead. A field
 * cleared to null is left untouched, since an override can't hold null.
 */
export const companyInfoWithEdits = (
  raw: client.CompanyInfo,
  originalSafe: client.RawCompanyInfo,
  safe: client.RawCompanyInfo
): client.CompanyInfo => {
  const next = cloneDeep(raw);
  for (const key of Object.keys(safe) as (keyof client.RawCompanyInfo)[]) {
    const value = safe[key];
    if (
      value !== undefined &&
      value !== null &&
      !isEqual(value, originalSafe[key])
    ) {
      next.private_overrides[key] = value;
    }
    if (isEqual(value, next.private_computed_values[key])) {
      delete next.private_overrides[key];
    }
  }
  return next;
};

/**
 * policy_info with ``edited_fields`` extended by every field that differs from
 * the saved one, which is how an edit survives a report rerun.
 */
export const policyInfoWithEditedFields = (
  policyInfo: client.PolicyInfo,
  original: client.PolicyInfo | null | undefined
): client.PolicyInfo => {
  type DataKey = keyof Omit<client.PolicyInfo, "edited_fields">;
  const edited = new Set(policyInfo.edited_fields ?? []);
  for (const key of Object.keys(policyInfo)) {
    if (key === "edited_fields") continue;
    const dataKey = key as DataKey;
    if (!isEqual(policyInfo[dataKey], original?.[dataKey])) edited.add(key);
  }
  return { ...policyInfo, edited_fields: [...edited] };
};
